import { useEffect, useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from '@/store/types';
import { useChat } from '@/hooks/useChat';
import { Layout, SidebarLayout } from '@/layouts';
import { useAuth } from '@/hooks/useAuth';
import { useSavedItems } from '@/hooks/useSavedItems';
import SettingsButton from '@/components/ui/settingsButton';
import LoginPrompt from "@/components/auth/LoginPrompt";
import { ItemTypeSaved, SharedItem } from "@/types/common";
import { TooltipProvider } from "@/components/ui/tooltip";
import KnowledgeSidebar from '@/components/knowledge/KnowledgeSidebar';
import KnowledgeContent from '@/components/knowledge/KnowledgeContent';
import { useDataManagement } from '@/hooks/useDataManagement';
import { useItemInteractions } from '@/hooks/useItemInteractions';
import { useSettings } from '@/hooks/useSettings';

const skipSupabaseFetch = async () => null;

export default function CustomPage() {
    const { route = '' } = useParams();
    const { user } = useAuth();
    const { settings, settingsLoaded } = useSettings();
    const { customPageData } = useSelector((state: RootState) => state.interview);
    const page = useMemo(
        () => (settings.customPages || []).find(item => item.route === route),
        [route, settings.customPages]
    );
    const pageKey = page ? `custom:${page.route}` : undefined;
    const customData = page ? customPageData[page.route] || [] : [];
    const { savedItems, saveItem, addFollowUpQuestion } = useSavedItems(ItemTypeSaved.KnowledgeAnswers);

    const {
        loading,
        selectedModel,
        setSelectedModel,
        generateAnswer,
        setAnswer,
        clearHistory
    } = useChat({ type: 'knowledge', pageKey }, user);

    const {
        expandedCategories,
        isLoading,
        selectedCategories,
        shuffledQuestions,
        setShuffledQuestions,
        searchQuery,
        isTagsExpanded,
        setSearchQuery,
        setIsTagsExpanded,
        toggleCategory,
        filterItems,
        handleCategorySelect,
        handleShuffleQuestions,
        handleApiKeySubmit,
    } = useDataManagement({
        dataType: 'custom',
        data: customData,
        fetchDataFromSupabase: skipSupabaseFetch,
        customRoute: page?.route,
        customSheetName: page?.sheetName
    });

    const {
        selectedItem,
        setSelectedItem,
        isSavedAnswer,
        setIsSavedAnswer,
        existingSavedItem,
        handleItemClick,
        handleRegenerateAnswer,
        error
    } = useItemInteractions({
        type: 'knowledge',
        generateAnswer,
        savedItems,
        clearHistory,
        selectedModel,
        pageKey,
        customPromptTemplates: page?.customPromptTemplates
    });

    useEffect(() => {
        if (existingSavedItem && existingSavedItem.id === (selectedItem as SharedItem)?.id) {
            setIsSavedAnswer(true);
        }
    }, [existingSavedItem, selectedItem, setIsSavedAnswer]);

    if (!user) {
        return <LoginPrompt onSuccess={() => window.location.reload()} />;
    }

    if (!settingsLoaded) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center text-gray-600">
                Loading...
            </div>
        );
    }

    if (!page) {
        return <Navigate to="/settings" replace />;
    }

    const handleShuffleQuestionsWithState = () => {
        handleShuffleQuestions(setSelectedItem, setAnswer);
    };

    return (
        <TooltipProvider>
            <Layout>
                <SidebarLayout
                    sidebar={
                        <KnowledgeSidebar
                            title={page.title}
                            knowledge={customData}
                            expandedCategories={expandedCategories}
                            searchQuery={searchQuery}
                            selectedQuestion={selectedItem}
                            toggleCategory={toggleCategory}
                            handleQuestionClick={handleItemClick}
                            filterQuestions={filterItems}
                            setSearchQuery={setSearchQuery}
                            shuffleQuestions={handleShuffleQuestionsWithState}
                            shuffledQuestions={shuffledQuestions}
                            setShuffledQuestions={setShuffledQuestions}
                            selectedCategories={selectedCategories}
                            handleCategorySelect={handleCategorySelect}
                            isTagsExpanded={isTagsExpanded}
                            setIsTagsExpanded={setIsTagsExpanded}
                            isLoading={isLoading}
                        />
                    }
                    content={
                        <KnowledgeContent
                            selectedQuestion={selectedItem}
                            user={user}
                            saveItem={saveItem}
                            selectedModel={selectedModel}
                            setSelectedModel={setSelectedModel}
                            handleRegenerateAnswer={handleRegenerateAnswer}
                            loading={loading}
                            error={error}
                            savedItems={savedItems}
                            addFollowUpQuestion={addFollowUpQuestion}
                            generateAnswer={generateAnswer}
                            setAnswer={setAnswer}
                            isSavedAnswer={isSavedAnswer}
                            setIsSavedAnswer={setIsSavedAnswer}
                            existingSavedItem={existingSavedItem}
                            typeSavedItem={ItemTypeSaved.KnowledgeAnswers}
                        />
                    }
                />
                <SettingsButton
                    onSubmit={handleApiKeySubmit}
                    sheetName={page.sheetName}
                    customPageRoute={page.route}
                    isLoading={isLoading}
                />
            </Layout>
        </TooltipProvider>
    );
}
